const REGEX = /(D{1,2})|(h{1,2})|(m{1,2})|(s{1,2})/g;

export enum Weekday {
    SUN = 0,
    MON = 1,
    TUE = 2,
    WED = 3,
    THU = 4,
    FRI = 5,
    SAT = 6,
}

export const SECOND_MS = 1000;
export const MINUTE_MS = 60 * SECOND_MS;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

export default class DateHelper {
    public static now(): Date {
        return getDate();
    }

    public static getDateFromValue(value: number | string): Date {
        return new Date(value);
    }

    public static timeTo(
        from: Date,
        to: Date,
        format: string = 'mm:ss'
    ): string {
        return DateHelper.format(to.getTime() - from.getTime(), format);
    }

    public static format(time: number, format: string): string {
        const D = Math.floor(time / DAY_MS);
        let h = Math.floor(time / HOUR_MS);
        let m = Math.floor(time / MINUTE_MS) % 60;
        let s = Math.floor(time / SECOND_MS) % 60;

        return format.replace(REGEX, match => {
            const len = match.length;
            switch (match[0]) {
                case 'D':
                    h = h % 24;
                    return len === 1 ? D.toString() : pad(D, 2);
                case 'h':
                    m = m % 60;
                    return len === 1 ? h.toString() : pad(h, 2);
                case 'm':
                    s = s % 60;
                    return len === 1 ? m.toString() : pad(m, 2);
                case 's':
                    return len === 1 ? s.toString() : pad(s, 2);
                default:
                    return match;
            }
        });
    }

    public static getUnixTimestampMS(date?: Date): number {
        return Math.floor(!!date ? +date : +new Date());
    }

    public static secondsToMilliseconds(seconds: number): number {
        return seconds * SECOND_MS;
    }

    public static minutesToMilliseconds(minutes: number): number {
        return minutes * MINUTE_MS;
    }

    public static hoursToMilliseconds(hours: number): number {
        return hours * HOUR_MS;
    }

    public static daysToMilliseconds(days: number): number {
        return days * DAY_MS;
    }

    /**
     * @param dayOfWeek The day of the week: Sunday (0) - Saturday (6).
     * @param date Instance of Javascript Date.
     * @param next Jump to the next weekday if it has already passed.
     */
    public static wrapDayOfWeek(weekday: Weekday, date: Date): Date {
        // Clone the date and set hours, minutes, seconds and milliseconds to 0.
        const newDate = DateHelper.getDateFromValue(date.valueOf());
        newDate.setUTCHours(0);
        newDate.setUTCMinutes(0);
        newDate.setUTCSeconds(0);
        newDate.setUTCMilliseconds(0);

        const diff = weekday - date.getUTCDay();
        if (date.getUTCDay() >= weekday) {
            newDate.setUTCDate(date.getUTCDate() + 7 + diff);
        } else {
            newDate.setUTCDate(date.getUTCDate() + diff);
        }

        return newDate;
    }

    public static getPreviousWeekday(weekday: Weekday, date: Date): Date {
        const newDate = new Date(date.valueOf());
        newDate.setUTCHours(0);
        newDate.setUTCMinutes(0);
        newDate.setUTCSeconds(0);
        newDate.setUTCMilliseconds(0);

        const diff = (7 - weekday + newDate.getUTCDay()) % 7;
        newDate.setUTCDate(newDate.getUTCDate() - diff);

        return newDate;
    }

    public static getNextWeekday(weekday: Weekday, date: Date): Date {
        const newDate = new Date(date.valueOf());
        newDate.setUTCHours(0);
        newDate.setUTCMinutes(0);
        newDate.setUTCSeconds(0);
        newDate.setUTCMilliseconds(0);

        const diff = (7 + weekday - newDate.getUTCDay()) % 7;
        newDate.setUTCDate(newDate.getUTCDate() + diff);

        return newDate;
    }

    public static getElapsedTime(date: number | string): number {
        return (
            DateHelper.now().getTime() -
            DateHelper.getDateFromValue(date).getTime()
        );
    }

    public static isTimeUp(date: number | string, cooldown: number): boolean {
        return DateHelper.getElapsedTime(date) > cooldown;
    }
}

function pad(num: string | number, zeroes: number = 2): string {
    return ('0'.repeat(zeroes) + num.toString()).slice(-zeroes);
}

let relativeDate: Date;
let startMS: number;

/**
 * Set the standard time retrieved from the server.
 * @param timestamp Server generated Unix timestamp in milliseconds.
 */
export function setServerDate(timestamp: number): void {
    //relativeDate = new Date(timestamp);

    // hotfix: new Date(timestamp) resulted in a date at the 1.1.1970
    // not really sure why window.perfomance.now was used since this only returns
    // a timestamp from the web page start, new Date() however returns the current
    // date which makes much more sense in this scenario
    relativeDate = new Date();
    startMS = Math.round(performance.now());
}

/**
 * Get a date object using relativeDate from server if it exists, otherwise local date.
 */
export function getDate(): Date {
    if (relativeDate !== undefined) {
        const relativeDateMS = +relativeDate;
        const endMS = Math.round(performance.now());

        // Add elapsed MS using `performance.now()`.
        return new Date(relativeDateMS + endMS - startMS);
    }

    return new Date();
}

/**
 * Get a UTC date string in the format yyyy-mm-dd hh:mm:ss.SSS
 */
export function getDateString(date?: Date): string {
    date = date || getDate();
    return (
        date.getUTCFullYear() +
        '-' +
        pad(date.getUTCMonth() + 1) +
        '-' +
        pad(date.getUTCDate()) +
        ' ' +
        pad(date.getUTCHours()) +
        ':' +
        pad(date.getUTCMinutes()) +
        ':' +
        pad(date.getUTCSeconds()) +
        '.' +
        pad(date.getUTCMilliseconds(), 3)
    );
}

/**
 * Get an SQL date format yyyy-mm-dd
 */
export function getSQLDate(date?: Date): string {
    date = date || getDate();
    return (
        date.getUTCFullYear() +
        '-' +
        pad(date.getUTCMonth() + 1) +
        '-' +
        pad(date.getUTCDate())
    );
}
